/*
 * DatePicker component is used to support sale channel content version features,
 * including change background color base on hove/selected/expired/occupied state, etc.
 * Author: Chris
 * CreatedAt: 2021-3-11
 */

import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

import { DatePicker } from 'antd';

import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import css from './SalesChannelVersionDatePicker.less';

const { RangePicker } = DatePicker;

interface DateRange {
  beginDate: string;
  beginTime: string;
  endDate: string;
  endTime: string;
}

interface IProps {
  style?: Record<string, unknown>;
  enableDateCheck: boolean;
  data: {
    timezoneGmt?: string;
    specifiedDate: DateRange;
    occupiedRanges: DateRange[];
  };
  onChange?: (beginDNT: Moment, endDNT: Moment) => void;
}

const iconUrl =
  'https://resource2.unifyxp.com/shopper-v2/h5/salechannel_version_picker_date_icon.svg';
const DATE_FORMAT = 'YYYY-MM-DD HH:mm';
const DEFAULT_DATE_FORMAT = 'MMM DD, YYYY HH:mm';
const MINUTES_IN_ONE_DAY = 24 * 60;
const MINUTES_IN_ONE_HOUR = 60;

const SalesChannelVersionDatePicker = ({
  style,
  enableDateCheck,
  data,
  onChange,
}: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const BEGIN_PLACEHOLDER = getI18n('general.startdate');
  const END_PLACEHOLDER = getI18n('general.enddate');
  const [hourCells, setHourCells] = useState(null);
  const [minuteCells, setMinuteCells] = useState(null);
  const [activeHour, setActiveHour] = useState(0);
  const [activeMinute, setActiveMinute] = useState(0);
  const [isOpen, setOpen] = useState(false);
  const [occupiedMinutes, setOccupiedMinutes] = useState(new Set());
  const [shouldDisableNoEndTime, setShouldDisableNoEndTime] = useState(false);
  const [theStartingDisabledDate, setTheStartingDisabledDate] = useState(null);

  // flag to indicate if beginDate input box is focusing right now
  const [isBeginInputActive, setIsBeginInputActive] = useState(false);
  // flag to indicate if endDate input box is focusing right now
  const [isEndInputActive, setIsEndInputActive] = useState(false);

  //active selected begin date and time in moment format
  const [beginDNT, setBeginDNT] = useState(null);

  //active selected end date and time in moment format
  const [endDNT, setEndDNT] = useState(null);

  const [endInputDom, setEndInputDom] = useState(null);
  // const [beginInputDom, setBeginInputDom] = useState(null);

  /**
   * init beginDate and endDate if any
   */
  useEffect(() => {
    const { beginDate, beginTime, endDate, endTime } = data.specifiedDate;
    //init begin date and time
    if (beginDate && beginTime) {
      setBeginDNT(moment(`${beginDate} ${beginTime}`, DATE_FORMAT));
    }

    //init end date and time
    if (endDate && endTime) {
      setEndDNT(moment(`${endDate} ${endTime}`, DATE_FORMAT));
    }
  }, []);

  /**
   * init occupied minutes
   */
  useEffect(() => {
    const { occupiedRanges } = data;
    if (occupiedRanges && occupiedRanges.length) {
      occupiedRanges.forEach((item) => {
        if (item.beginDate && item.beginTime && item.endDate && item.endTime) {
          const tempBeginDNT = `${item.beginDate} ${item.beginTime}`;
          const beginInSecond = moment(tempBeginDNT, DATE_FORMAT).unix();
          const tempEndDNT = `${item.endDate} ${item.endTime}`;
          const endInSecond = moment(tempEndDNT, DATE_FORMAT).unix();
          const today = moment().hour(0).minute(0).second(0).unix();
          for (
            let second = beginInSecond;
            second <= endInSecond;
            second += 60
          ) {
            //filter old records earlier than today
            if (endInSecond >= today) {
              occupiedMinutes.add(second);
            }
          }
        }
      });

      setOccupiedMinutes(occupiedMinutes);
    }
  }, []);

  /**
   * setup listener for clear date picker content
   */
  useEffect(() => {
    const pickerDom = document.querySelector('div.ant-picker.ant-picker-range');
    if (pickerDom) {
      pickerDom.addEventListener('mouseover', (_) => {
        const pickerClearDom = document.querySelector('span.ant-picker-clear');
        if (pickerClearDom) {
          pickerClearDom.addEventListener('click', (_) => {
            setBeginDNT(null);
            setEndDNT(null);
          });
        }
      });
    }
  }, []);

  /**
   * setup listener for date picker focus changing
   */
  useEffect(() => {
    //get begin date input dom
    const _beginDateDOM = document.querySelector(
      `input[placeholder='${BEGIN_PLACEHOLDER}']`,
    );
    if (_beginDateDOM) {
      // setBeginInputDom(_beginDateDOM);
      _beginDateDOM.addEventListener('focus', (_) => {
        setIsBeginInputActive(true);
        setIsEndInputActive(false);
      });
    }

    //get end date input dom
    const _endDateDOM = document.querySelector(
      `input[placeholder='${END_PLACEHOLDER}']`,
    );
    if (_endDateDOM) {
      setEndInputDom(_endDateDOM);
      _endDateDOM.addEventListener('focus', (_) => {
        setIsBeginInputActive(false);
        setIsEndInputActive(true);
      });
    }
  }, []);

  /**
   * 1. setup listener for hour and minute clicking
   * 2. render customized 'Apply' button
   * 3. notify parent component when finishing choosing date and time
   */
  useEffect(() => {
    if (isOpen) {
      registerHourAndMinuteClickEvent();
      renderApplyButton();
    } else {
      unRegisterHourAndMinuteClickEvent();

      if (onChange) {
        onChange(beginDNT, endDNT);
      }
    }
  }, [isOpen]);

  /**
   * update beginDNT/endDNT when hour changing
   */
  useEffect(() => {
    if (isBeginInputActive && beginDNT) {
      setBeginDNT(moment(beginDNT).hour(activeHour));
    }

    if (isEndInputActive && endDNT) {
      setEndDNT(moment(endDNT).hour(activeHour));
    }
  }, [activeHour]);

  /**
   * update beginDNT/endDNT when minute changing
   */
  useEffect(() => {
    if (isBeginInputActive && beginDNT) {
      setBeginDNT(moment(beginDNT).minute(activeMinute));
    }

    if (isEndInputActive && endDNT) {
      setEndDNT(moment(endDNT).minute(activeMinute));
    }
  }, [activeMinute]);

  /**
   * 1. update shouldDisableNoEndTime to render 'No end time' button
   * 2. search to find 1st occupied whole day immediate after beginDate
   */
  useEffect(() => {
    if (
      enableDateCheck &&
      beginDNT &&
      occupiedMinutes &&
      occupiedMinutes.size
    ) {
      const beginDate = {
        year: moment(beginDNT).year(),
        month: moment(beginDNT).month(),
        date: moment(beginDNT).date(),
      };

      const beginSecond = moment(beginDate).unix();
      const occupiedMinutesArray = Array.from(occupiedMinutes);
      const result = occupiedMinutesArray.filter(
        (item: number) => item > beginSecond,
      );
      // 1. update shouldDisableNoEndTime to render 'No end time' button
      setShouldDisableNoEndTime(Boolean(result.length));

      // 2. search to find 1st occupied whole day immediate after beginDate
      let foundTheDate;
      let nextDayBegin;
      let nextDayBeginSecond;
      let nextDayEndSecond;
      let startIndex;
      let endIndex;
      let continueSearch = true;

      const lastItem = occupiedMinutesArray[
        occupiedMinutesArray.length - 1
      ] as Moment;

      for (let index = 0; continueSearch; index++) {
        nextDayBegin = moment(beginDate).add(index + 1, 'days');
        nextDayBeginSecond = nextDayBegin.unix();
        nextDayEndSecond = moment(nextDayBegin)
          .add(1, 'days')
          .subtract(1, 'minutes')
          .unix();

        if (nextDayEndSecond > lastItem) {
          continueSearch = false;
        }

        startIndex = occupiedMinutesArray.indexOf(nextDayBeginSecond);
        endIndex = occupiedMinutesArray.indexOf(nextDayEndSecond);

        if (startIndex === -1) {
          continue;
        }

        if (endIndex === -1) {
          continue;
        }

        const howMany = endIndex - startIndex + 1;
        if (howMany === MINUTES_IN_ONE_DAY) {
          foundTheDate = nextDayBeginSecond;
          continueSearch = false;
        }
      }

      if (foundTheDate) {
        setTheStartingDisabledDate(nextDayBegin);
      }
    }
  }, [beginDNT]);

  /**
   *  render customized button - 'Current time' and 'No end time'
   * @param panelNode
   */
  const handleRenderPanel = (panelNode) => {
    const handleClickTimeButton = () => {
      if (isBeginInputActive) {
        //click 'Current time' button
        endInputDom.focus();
        setBeginDNT(moment());
      } else if (isEndInputActive) {
        if (enableDateCheck && shouldDisableNoEndTime) {
          return;
        }

        //click 'No end time' button
        setEndDNT(null);
        setOpen(false);
      }
    };

    const getCss = () => {
      if (!enableDateCheck) {
        return `${css.timeButton}`;
      }

      if (isEndInputActive && shouldDisableNoEndTime) {
        return `${css.timeButton} ${css.disabledButton}`;
      } else {
        return `${css.timeButton}`;
      }
    };

    return (
      <div className={css.panel}>
        <div>{panelNode}</div>
        <div className={css.footer}>
          <div className={getCss()} onClick={handleClickTimeButton}>
            {isBeginInputActive
              ? getI18n('general.rangePicker.current_time')
              : getI18n('general.rangePicker.no_end')}
          </div>
        </div>
      </div>
    );
  };

  const handleDateClick = (e, current, _) => {
    if (isBeginInputActive) {
      console.log('begin now=', current);
    } else if (isEndInputActive) {
      console.log('end now=', current);
    }
  };

  /**
   * render each day
   * @param current - each day
   * @param today
   */
  const handleRenderDate = (current: Moment, today: Moment) => {
    return (
      <div
        className={`ant-picker-cell-inner`}
        onClick={(e) => handleDateClick(e, current, today)}>
        {current.date()}
      </div>
    );
  };

  const handleClickOk = (dates: [Moment, Moment]) => {
    const [begin, end] = dates;
    if (begin) {
      setBeginDNT(begin);
    }

    if (end) {
      setEndDNT(end);
    }
  };

  const handleDisabledDate = (currentDate: Moment): boolean => {
    if (!enableDateCheck) {
      return false;
    }

    if (!currentDate) {
      return true;
    }

    if (currentDate < moment().startOf('day')) {
      return true;
    }

    if (isEndInputActive && !isBeginInputActive) {
      if (theStartingDisabledDate) {
        return currentDate.unix() >= theStartingDisabledDate.unix();
      }
    }

    const date = {
      year: moment(currentDate).year(),
      month: moment(currentDate).month(),
      date: moment(currentDate).date(),
    };

    const beginSecond = moment(date).unix();
    const endSecond = moment(date).add(1, 'days').subtract(1, 'minutes').unix();

    const occupiedMinutesArray = Array.from(occupiedMinutes);
    const startIndex = occupiedMinutesArray.indexOf(beginSecond);
    const endIndex = occupiedMinutesArray.indexOf(endSecond);

    if (startIndex === -1) {
      return false;
    }

    if (endIndex === -1) {
      return false;
    }

    const howMany = endIndex - startIndex + 1;
    return howMany === MINUTES_IN_ONE_DAY;
  };

  const handleDisabledTime = (currentDate: Moment) => {
    const disabledHours = () => {
      if (!enableDateCheck) {
        return [];
      }

      const date = {
        year: moment(currentDate).year(),
        month: moment(currentDate).month(),
        date: moment(currentDate).date(),
      };

      const _disabledHours = [];
      for (let hour = 0; hour <= 23; hour++) {
        const beginMinute = moment(date).hour(hour).minute(0).unix();
        const endMinute = moment(date).hour(hour).minute(59).unix();
        const occupiedMinutesArray = Array.from(occupiedMinutes);
        const startIndex = occupiedMinutesArray.indexOf(beginMinute);
        const endIndex = occupiedMinutesArray.indexOf(endMinute);

        if (startIndex === -1) {
          continue;
        }

        if (endIndex === -1) {
          continue;
        }

        const howMany = endIndex - startIndex + 1;
        if (howMany === MINUTES_IN_ONE_HOUR) {
          _disabledHours.push(hour);
        }
      }

      return _disabledHours;
    };

    const disabledMinutes = () => {
      if (!enableDateCheck) {
        return [];
      }

      const date = {
        year: moment(currentDate).year(),
        month: moment(currentDate).month(),
        date: moment(currentDate).date(),
        hour: moment(currentDate).hour(),
      };

      const _disabledMinutes = [];
      for (let minute = 0; minute <= 59; minute++) {
        const tempMinute = moment(date).minute(minute).unix();
        const occupiedMinutesArray = Array.from(occupiedMinutes);
        const index = occupiedMinutesArray.indexOf(tempMinute);
        if (index !== -1) {
          _disabledMinutes.push(minute);
        }
      }

      return _disabledMinutes;
    };
    return { disabledHours, disabledMinutes };
  };

  const handleOpen = (open) => {
    setOpen(open);
  };

  const registerHourAndMinuteClickEvent = () => {
    const _hourCells = document.querySelectorAll(
      'div.ant-picker-content > :nth-child(1) > .ant-picker-time-panel-cell',
    );
    if (_hourCells) {
      setHourCells(_hourCells);
      _hourCells.forEach((cell) => {
        // bind 'click' listener on non-disabled hour cell
        cell.addEventListener('click', (_) => {
          const classAttribute = cell.getAttribute('class');
          if (
            classAttribute !== null &&
            classAttribute.indexOf('ant-picker-time-panel-cell-disabled') === -1
          ) {
            setActiveHour(parseInt(cell.innerHTML));
          }
        });
      });
    }

    const _minuteCells = document.querySelectorAll(
      'div.ant-picker-content > :nth-child(2) > .ant-picker-time-panel-cell',
    );
    if (_minuteCells) {
      setMinuteCells(_minuteCells);
      _minuteCells.forEach((cell) => {
        cell.addEventListener('click', (_) => {
          const classAttribute = cell.getAttribute('class');
          if (
            classAttribute !== null &&
            classAttribute.indexOf('ant-picker-time-panel-cell-disabled') === -1
          ) {
            setActiveMinute(parseInt(cell.innerHTML));
          }
        });
      });
    }
  };

  const unRegisterHourAndMinuteClickEvent = () => {
    if (hourCells) {
      hourCells.forEach((cell) => {
        cell.removeEventListener('click', null);
      });
    }

    if (minuteCells) {
      minuteCells.forEach((cell) => {
        cell.removeEventListener('click', null);
      });
    }
  };

  const renderSuffixIcon = () => {
    return (
      <img
        src={iconUrl}
        alt='sale-channel-picker-icon'
        style={{ width: 12, height: 12, marginTop: 1, opacity: 0.6 }}
      />
    );
  };

  const renderApplyButton = () => {
    const pickerFooterDom = document.querySelector(
      'div.ant-picker-footer',
    ) as HTMLElement;
    if (pickerFooterDom) {
      pickerFooterDom.style.height = '56px';
    }

    const pickerRangeDom = document.querySelector('ul.ant-picker-ranges');
    if (pickerRangeDom) {
      const buttonContainerDom = pickerRangeDom.firstChild as HTMLElement;
      if (buttonContainerDom) {
        buttonContainerDom.style.marginTop = '7px';
        const okButtonDom = buttonContainerDom.firstChild as HTMLElement;
        if (okButtonDom) {
          okButtonDom.style.width = '80px';
          okButtonDom.style.height = '32px';
          okButtonDom.style.borderRadius = '4px';
          const span = okButtonDom.firstChild as HTMLElement;
          span.innerHTML = 'Apply';
          span.style.fontSize = '14px';
          span.style.fontWeight = '600';
        }
      }
    }
  };

  return (
    <RangePicker
      className={css.rangePicker}
      style={style ? { ...style } : {}}
      value={[beginDNT, endDNT]}
      dateRender={(current, today) => handleRenderDate(current, today)}
      disabledDate={handleDisabledDate}
      disabledTime={handleDisabledTime}
      suffixIcon={renderSuffixIcon()}
      panelRender={handleRenderPanel}
      onOpenChange={handleOpen}
      onOk={handleClickOk}
      open={isOpen}
      showTime={{
        showSecond: false,
        format: 'HH:mm',
        showNow: true,
      }}
      format={DEFAULT_DATE_FORMAT}
      allowClear
      placeholder={[`${BEGIN_PLACEHOLDER}`, `${END_PLACEHOLDER}`]}
      disabled={[false, false]}
    />
  );
};

export default SalesChannelVersionDatePicker;
